import './Character.scss'

import { useCharacters } from "../../../contexts/Characters"
import { useEffect, useState } from 'react'
import { capitalize, deepMatch, formatThousands } from '../../../utilities/helpers'
import { Roles, timeSlots, unitTypes } from '../../../utilities/constants'
import { useNavigate, useParams } from 'react-router'
import { useModal } from '../../../contexts/Modal'
import { useToaster } from '../../../contexts/Toaster'
import { DateTime } from 'luxon'
import Tooltip from '../../core/Tooltip/Tooltip'
import WriteKvk from '../../modals/WriteKvkPerformance/WiteKvkPerformance'
import Select from '../../forms/Select/Select'
import useFormBuilder from '../../../hooks/FormBuilderHook'
import koalition from '../../../api/koalition'
import EditMercenaryTech from '../../modals/EditMercenaryTech/EditMercenaryTech'
import FixedMgeForm from '../../domain/FixedMgeForm/FixedMgeForm'
import EditCharacterInfo from '../../modals/EditCharacterInfo/EditCharacterInfo'
import useConfirmation from '../../../hooks/ConfimationHook'
import Checkbox from '../../forms/Checkbox/Checkbox'
import Validators from '../../../forms/Validators'
import EditSeasonalRecord from '../../modals/EditSeasonalRecord/EditSeasonalRecord'

const Characters = () => {
    const characters = useCharacters()
    const toaster = useToaster()
    const navigate = useNavigate()
    const { characterId } = useParams()
    const [character, setCharacter] = useState()
    const modal = useModal()
    const mercenaryTechStages = 2
    const troopTierOptions = Array(9).fill().map((_, index) => ({ value: index + 1, label: `T${index + 1}` })).toReversed()
    const canEdit = characters.$state?.current && (characters.authorized([Roles.developer, Roles.admin]) || characters.$state.current._id === character?._id)
    const { isMarked, markOrExecute } = useConfirmation()
    const timeSlotsDays = [timeSlots.slice(0, 3), timeSlots.slice(3)]
    const availabilitiesForm = useFormBuilder({ controls: {
        availabilities: { validators: [Validators.required] },
        availabilitiesExpiration: {},
        resetAvailabilities: {}
    }})
    
    const troopTiersForm = useFormBuilder({ controls: Object.fromEntries(unitTypes.map((unitType) => [unitType, {}])) })

    const onGeneralInfoEditClick = () => {
        modal.open({ title: 'Edit Info', component: <EditCharacterInfo />, data: { character } })
    }

    const onSeasonalRecordEditClick = (record) => {
        modal.open({ title: 'Edit Seasonal Record', component: <EditSeasonalRecord />, data: { character, record } })
    }

    const onKvkPerformanceEditClick = (kvk) => {
        modal.open({ title: 'Edit Kvk Performance', component: <WriteKvk />, data: { character, kvk } })
    }
    
    const onMercenaryTechEditClick = () => {
        modal.open({ title: 'Edit Mercenary Tech', component: <EditMercenaryTech />, size: 'lg', data: { character } })
    }

    const onAvailabilitiesSaveClick = () => {
        const formValues = availabilitiesForm.$state.value

        koalition.put(`characters/${character._id}`, formValues).then((response) => {
            modal.close()
            toaster.success('Character updated successfully')
        }).catch((error) => {
            toaster.error(error)
            console.error(error)
        })
    }

    const onUserClick = (userId) => {
        navigate(`/users/${userId}`)
    }

    const deleteCharacter = () => {
        navigate('/characters')
        koalition.delete(`/characters/${character._id}`).then((response) => {
            toaster.success(`Character ${character.name} was deleted successfully`)
        }).catch((error) => {
            toaster.error(error)
            console.error(error)
        })
    }

    const updateTroopTier = (troopName, troopTier) => {
        if (character.troopTiers[troopName] !== troopTier) {
            character.troopTiers[troopName] = troopTier
            koalition.put(`characters/${character._id}`, { troopTiers: character.troopTiers }).then((response) => {
                toaster.success('Troop tier updated successfully')
            }).catch((error) => {
                toaster.error(error)
                console.error(error)
            })
        }
    }

    const toggleTimeSlot = (timeSlot) => {
        const availabilities = availabilitiesForm.$state.controls.availabilities.value
        const newValue = availabilities.includes(timeSlot) 
            ? availabilities.filter((availability) => availability !== timeSlot) 
            : [...availabilities, timeSlot]

        availabilitiesForm.$state.controls.availabilities.setValue(newValue)
    }

    const onResetAvailabilitiesChange = (expire) => {
        availabilitiesForm.$state.controls.availabilitiesExpiration.setValue(expire ? DateTime.utc().endOf('week').ts : null)
    }

    useEffect(() => {
        if (characters.$state?.server) {
            const currentCharacter = characters.$state.server.list.find((character) => character._id === characterId)
            setCharacter(currentCharacter)
            availabilitiesForm.$state.controls.availabilities.setValue(currentCharacter.availabilities)
            availabilitiesForm.$state.controls.availabilitiesExpiration.setValue(currentCharacter.availabilitiesExpiration)
            availabilitiesForm.$state.controls.resetAvailabilities.setValue(!!currentCharacter.availabilitiesExpiration)
            Object.entries(currentCharacter.troopTiers).forEach(([troopName, troopTier]) => {
                troopTiersForm.$state.controls[troopName].setValue(troopTier)
            })
        }
    }, [characters.$state, characterId])

    return (
        <div className="container-fluid character-profile">
            {character && (
                <>
                    <header className="row mb-4">
                        {character.alliance && <div className="col-auto pr-0"><span className="chip primary">{character.alliance?.tag}</span></div>}
                        <h1 className="col-auto">{character.name}</h1>
                        <div className="col-auto ml-auto">
                            {canEdit && (
                                <Tooltip text="Edit Character" align="right">
                                    <button className="primary medium icon" onClick={onGeneralInfoEditClick}>
                                        <img src="/images/icons/pencil.svg" />
                                    </button>
                                </Tooltip>
                            )}
                            {characters.authorized([Roles.developer, Roles.admin]) && (
                                <Tooltip text="Delete Character" align="right">
                                    <button className={`${isMarked(character._id) ? 'danger outline marked' : 'primary'} medium icon ml-3`} onClick={() => markOrExecute(character._id, deleteCharacter)}>
                                        <img src="/images/icons/trash.svg" />
                                    </button>
                                </Tooltip>
                            )}
                            {characters.authorized([Roles.developer]) && (
                                <Tooltip text="Show User" align="right">
                                    <button className="primary medium icon ml-3" onClick={() => onUserClick(character.user)}>
                                        <img src="/images/icons/person.svg" />
                                    </button>
                                </Tooltip>
                            )}
                        </div>
                    </header>
                    
                    <main className="row mb-5">
                        <section className="col-sm col-lg-auto general-info mb-4">
                            <div className="card base h-100">
                                <h3 className="text-center mb-4">General Info</h3>
                                <ul>
                                    <li className="row justify-content-between mb-2">
                                        <span className="col-auto"><b>Power: </b></span>
                                        <span className="col-auto">{formatThousands(character.power)}</span>
                                    </li>
                                    <li className="row justify-content-between mb-2">
                                        <span className="col-auto"><b>Merits: </b></span>
                                        <span className="col-auto">{formatThousands(character.merits)}</span>
                                    </li>
                                </ul>
                            </div>
                        </section>

                        <section className="col-sm col-lg-auto troop-tiers mb-4">
                            <div className="card base h-100">
                                <h3 className="text-center mb-4">Troop Tiers</h3>
                                <ul>
                                    {troopTiersForm.$state && unitTypes.map((troopName) => (
                                        <li className="row justify-content-between align-items-center my-1" key={troopName}>
                                            <span className="col-auto mr-3">{capitalize(troopName)}</span>
                                            <div className="col-auto">
                                                {canEdit ? (
                                                    <Select options={troopTierOptions} control={troopTiersForm.$state.controls[troopName]} className="chip base dark troop-tier p-1" onChange={(event) => updateTroopTier(troopName, event)} />
                                                ) : (
                                                    <span className="chip base dark troop-tier flex-center p-1">T{character.troopTiers[troopName]}</span>
                                                )}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </section>

                        <section className="col-lg-auto mb-4">
                            <div className="card base h-100">
                                <header className="row">
                                    <h3 className="col text-center mb-4">Mercenary Tech</h3>
                                    {canEdit && (
                                        <div className="col-auto">
                                            <Tooltip text="Edit">
                                                <button className="primary small icon p-2" onClick={onMercenaryTechEditClick}>
                                                    <img src="/images/icons/pencil.svg" />
                                                </button>
                                            </Tooltip>
                                        </div>
                                    )}
                                </header>

                                <div className="row h-100">
                                    {Array(mercenaryTechStages).fill().map((_, index) => (
                                        <div className="col" key={index}>
                                            <div className="card base semi-dark mb-3">
                                                <h4 className="mb-3">Stage {index + 1}</h4>
                                                <ul>
                                                    {unitTypes.map((unitType) => (
                                                        <li className="row justify-content-between align-items-center flex-nowrap my-2" key={unitType}>
                                                            <span className="col-auto mr-3">{capitalize(unitType)}</span>
                                                            <span className="col-auto">{character[`mercenaryTech${index + 1}`][unitType]}%</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>

                        {availabilitiesForm.$state && (
                            <section className="col-lg-auto legions mb-4">
                                <div className="card base h-100">
                                    <div className="row flex-column h-100">
                                        <h3 className="col-auto text-center mb-4">Legions Intentions</h3>

                                        <div className="col-auto time-slots">
                                            {timeSlotsDays.map((timeSlotDay, dayIndex) => (
                                                <ul className="flex-center" key={dayIndex}>
                                                    {timeSlotDay.map((timeSlot) => (
                                                        <li className="time-slot" onClick={() => toggleTimeSlot(timeSlot)} key={timeSlot}>
                                                            <span className={`chip ${availabilitiesForm.$state.controls.availabilities.value.includes(timeSlot) ? 'primary' : 'base'} mr-3 mb-3`}><b>{timeSlot}</b></span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ))}
                                        </div>

                                        <div className="col-auto mt-auto">
                                            <footer className="row justify-content-between">
                                                <div className="col-auto">
                                                    <Checkbox control={availabilitiesForm.$state.controls.resetAvailabilities} onChange={onResetAvailabilitiesChange} label="Reset after this week" />
                                                </div>
                                                <div className="col-auto">
                                                    <button className="primary medium" onClick={onAvailabilitiesSaveClick}>Save</button>
                                                </div>
                                            </footer>
                                        </div>
                                    </div>

                                </div>
                            </section>
                        )}
                    </main>

                    {!!character.seasonalRecords?.length && (
                        <section className="seasonal-records mb-5">
                            <h2 className="mb-4">Seasonal Performance</h2>
                            <ul className="d-flex flex-nowrap seasons">
                                {character.seasonalRecords.sort((r1, r2) => r2.stellarSeason.name - r1.stellarSeason.name).map((record, index) => (
                                    <li className={`col-11 col-md-7 col-lg-5 col-xl-4 pr-0 ${index ? '' : 'pl-0'} pb-3`} key={record.stellarSeason._id}>
                                        <div className="card base season h-100">
                                            <header className="row justify-content-between">
                                                <h3 className="col-auto mb-3">Season {record.stellarSeason.name}</h3>
                                                <div className="col-auto mb-3">
                                                    {characters.authorized([Roles.developer, Roles.admin]) && (
                                                        <Tooltip text="Edit" position="bottom">
                                                            <button className="primary small icon p-2" onClick={() => onSeasonalRecordEditClick(record)}>
                                                                <img src="/images/icons/pencil.svg" />
                                                            </button>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            </header>

                                            <div className="card semi-dark mb-3">
                                                <div className="row justify-content-between mb-3">
                                                    <div className="col-auto"><b>Seasonal Merits:</b></div>
                                                    <div className="col-auto">{formatThousands(record.seasonalMerits)}</div>
                                                </div>
                                                {!!record.receivedFixedMges && (
                                                    <div className="row justify-content-between">
                                                        <div className="col-auto"><b>Fixed MGEs:</b></div>
                                                        <div className="col-auto">{record.receivedFixedMges}</div>
                                                    </div>
                                                )}
                                                {!!record.penalties && (
                                                    <div className="row justify-content-between">
                                                        <div className="col-auto"><b>Penalties:</b></div>
                                                        <div className="col-auto">{record.penalties}</div>
                                                    </div>
                                                )}
                                                {!!record.noShows && (
                                                    <div className="row justify-content-between">
                                                        <div className="col-auto"><b>No Shows:</b></div>
                                                        <div className="col-auto">{record.noShows}</div>
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {!!record.stellarSeason.kvks.length && (
                                                <div className="card semi-dark">
                                                    <ul>
                                                        {record.stellarSeason.kvks.map((kvk, index) => (
                                                            <li className="kvk my-2" key={kvk._id}>
                                                                {!!index && <hr className="my-2" />}

                                                                <div className="row align-items-center">
                                                                    {canEdit && (
                                                                        <div className="col-auto pr-0">
                                                                            <Tooltip text="Edit Kvk">
                                                                                <button className="primary tiny icon p-2" onClick={() => onKvkPerformanceEditClick(kvk)}>
                                                                                    <img src="/images/icons/pencil.svg" />
                                                                                </button>
                                                                            </Tooltip>
                                                                        </div>
                                                                    )}
                                                                    <div className="col-auto"><b>{kvk.type.toUpperCase()}</b></div>

                                                                    {kvk.performances[character._id]?.position && (
                                                                        <div className="col-auto ml-auto">
                                                                            <span className="chip primary small flex-center my-1">
                                                                                <img className="icon" src="/images/icons/crown.svg" />
                                                                                <span className="ml-2">{kvk.performances[character._id].position}</span>
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {!!kvk.performances[character._id]?.score && (
                                                                    <div className="row justify-content-end">
                                                                        <div className="col-auto score mt-1">{formatThousands(kvk.performances[character._id].score)}</div>
                                                                    </div>
                                                                )}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    <section>
                        <h2 className="mb-4">Fixed MGE Application</h2>
                        <div className="card mild-dark">
                            <FixedMgeForm character={character} />
                        </div>
                    </section>
                </>
            )}
        </div>
    )
}

export default Characters